import { z } from 'zod';
import { ActForRole } from './acting-for-base.js';
import { PlainDate } from './common.js';
import { OperationalAwardTypeCode } from './awards.js';
import { LevelOne, RoleAssignment, RoleKey } from './me.js';
import { PageInfo, pageFields } from './paging.js';

/**
 * The System Admin area (SYS1–SYS8, FR5, TEST1) and the role-scoped Audit log (APP-044). Every
 * change here is audited in the PRD §17 format; nothing that has been used is hard-deleted.
 */

const Ref = z.object({ id: z.string(), name: z.string() });
const PersonRef = z.object({ id: z.uuid(), name: z.string(), jobTitle: z.string().nullable() });
const Count = z.number().int().nonnegative();
const DateTime = z.string();
/** A reason the screen asks for. Required: say why. */
const Reason = z.string().trim().min(1, 'Say why.').max(2000);
/** A reason the screen offers but doesn't require. */
const OptionalReason = z.string().trim().max(2000).optional();
const ReasonOnly = z.object({ reason: OptionalReason }).strict();

/** "Last changed by [person] on [date]", with the reason when one was given (PRD §17). */
export const LastChanged = z.object({
  by: Ref.nullable(),
  /** "Directory sync", "Seed data", … when no person made the change. */
  byLabel: z.string(),
  at: DateTime,
  reason: z.string().nullable(),
});
export type LastChanged = z.infer<typeof LastChanged>;

// ---------------------------------------------------------------------------------------------
// Audit log (SYS4) — read-only for everyone who sees it, scoped by role (APP-028, APP-030)
// ---------------------------------------------------------------------------------------------

export const AuditAreaKey = z.enum([
  'budgets',
  'values',
  'charge_codes',
  'sector_setup',
  'requesters',
  'elections_letters',
  'tax_estimate',
  'directory_roles',
  'system',
  'awards',
  'payroll',
  'access',
  'personal',
  'feedback',
]);
export type AuditAreaKey = z.infer<typeof AuditAreaKey>;

export const AuditSourceKey = z.enum([
  'screen',
  'csv_import',
  'directory_sync',
  'system_rule',
  'email',
  'teams',
  'setup_file',
]);
export type AuditSourceKey = z.infer<typeof AuditSourceKey>;

export const AuditActionKey = z.enum([
  'create',
  'update',
  'delete',
  'restore',
  'view',
  'sign_in',
  'sign_out',
  'preview',
]);

const AuditValue = z.object({ value: z.unknown(), unit: z.string().optional() });

/** One entry in the PRD §17 audit standard, plus who may read it (area and sector). */
export const AuditEntry = z.object({
  id: z.uuid(),
  /** Its place in the hash chain; newer entries have larger numbers. */
  position: z.number().int(),
  occurredAt: DateTime,
  actor: z.object({
    personId: z.string().nullable(),
    name: z.string(),
    roles: z.array(z.string()),
    /** APP-122: the person signed in, when they acted as the actor in Preview as (dev and test). */
    previewedBy: Ref.nullable().optional(),
  }),
  onBehalfOf: Ref.nullable(),
  action: AuditActionKey,
  entity: z.object({ type: z.string(), id: z.string(), label: z.string() }),
  field: z.string().nullable(),
  before: AuditValue.nullable(),
  after: AuditValue.nullable(),
  effectiveFrom: PlainDate.nullable(),
  reason: z.string().nullable(),
  source: AuditSourceKey,
  batchId: z.string().nullable(),
  area: AuditAreaKey.nullable(),
  sectorId: z.string().nullable(),
  /**
   * Words for the IDs an older entry saved alone (APP-044 §44): ID → name, for the entry's own
   * people, program, files, pools and Entra object IDs. Never part of the stored entry; absent
   * when there's nothing to name. Newer entries save `{ id, name }` themselves.
   */
  names: z.record(z.string(), z.string()).optional(),
  /**
   * APP-148 D26: the action a Stock Admin did for an Executive or a Sector Lead in the same
   * request (its `acted_for` record), for the For column and the entry's "For" and "Files": the
   * role the person held for it then, and the award it was done on. On the action's own entry
   * (its Stage, else its first), the Stock Admin's note and how many files are kept with it, for
   * those who may read what was done for that person (R41); null for anyone else and on every
   * other entry. Never part of the stored entry; null when no one acted for someone (a delegate's
   * entry for their Sector Lead: its role is in `forRole`). Optional: older servers omit it.
   */
  actedFor: z
    .object({
      forRole: ActForRole,
      /** As recorded then: "Executive", "Sector Lead · Terrestrial". */
      forRoleLabel: z.string(),
      award: z.object({ id: z.uuid(), displayId: z.string().nullable() }).nullable(),
      /** This is the action's own entry, where its note and files are shown. */
      own: z.boolean(),
      note: z.string().nullable(),
      files: Count.nullable(),
    })
    .nullable()
    .optional(),
  /**
   * APP-148 R144: the role the person it was made for held for it, for the For column and the
   * entry's "For": as the action recorded it when a Stock Admin acted (`actedFor`), else their
   * lead of the entry's sector or their place on the Executives list at its time (a draft started
   * for a Sector Lead, a delegate's entry). Null when it names no one, or neither held (never
   * guessed). Never part of the stored entry. Optional: older servers omit it.
   */
  forRole: z.object({ role: ActForRole, label: z.string() }).nullable().optional(),
});
export type AuditEntry = z.infer<typeof AuditEntry>;

const AuditFilters = {
  area: AuditAreaKey.optional(),
  source: AuditSourceKey.optional(),
  /** Everything by, for or about one person: the actor, on behalf of, or the entity itself. */
  person: z.uuid().optional(),
  /**
   * APP-148 D26: only entries made on someone's behalf (a Stock Admin for an Executive or a Sector
   * Lead, a delegate for their lead), and only those for one person. In a query string, "true".
   */
  forSomeoneElse: z
    .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
    .optional(),
  forPerson: z.uuid().optional(),
  /**
   * APP-148 R97: only entries of actions the Stock Admin team did for an Executive or a Sector
   * Lead (an `acted_for` record has the entry's request and person), never a delegate's for their
   * lead. Roles & lists' counts open it. In a query string, "true".
   */
  actedFor: z
    .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
    .optional(),
  /** Only what this person did, as the actor (not entries about them, for them or previewed). */
  actor: z.uuid().optional(),
  entityType: z.string().trim().min(1).max(60).optional(),
  entityId: z.string().trim().min(1).max(200).optional(),
  /** An award's display ID (`SDB-2026-0151`) or ID: the award, its lines and its files. */
  award: z.string().trim().min(1).max(60).optional(),
  /** Words in the label, field or reason ("FMV per share"). Letters only, never a pattern. */
  q: z.string().trim().min(1).max(200).optional(),
  /** Inclusive dates, in UTC like the log's times. */
  from: PlainDate.optional(),
  to: PlainDate.optional(),
};

export const AuditLogQuery = z
  .object({
    ...AuditFilters,
    ...pageFields,
    /** The newest chain position page 1 could see (over everything the reader may read); later pages stop there so new entries don't shift rows (APP-135). */
    upTo: z.coerce.number().int().min(1).optional(),
  })
  .strict();
export type AuditLogQuery = z.infer<typeof AuditLogQuery>;

export const AuditExportQuery = z.object(AuditFilters).strict();
export type AuditExportQuery = z.infer<typeof AuditExportQuery>;

export const AuditLogPage = z.object({
  items: z.array(AuditEntry),
  page: PageInfo,
  /** The newest chain position the reader could see when page 1 was read, over everything they may read, filters aside; send it back as `upTo`. Null when there are none. */
  upTo: z.number().int().nullable(),
  /** Entries per source under the other filters: the source chips' counts. */
  sources: z.record(AuditSourceKey, Count),
  /** All the reader may see under the filters, before paging. */
  total: Count,
  /**
   * What this reader sees: `all` (Executives, Stock Admins, System Admins), `partial` (their own
   * sector, financial or payroll entries), or `none`.
   */
  scope: z.enum(['all', 'partial', 'none']),
});
export type AuditLogPage = z.infer<typeof AuditLogPage>;

/** The hash chain's last check (APP-012): the nightly run, or one an Admin asked for. */
export const AuditChainCheck = z.object({
  at: DateTime,
  trigger: z.enum(['schedule', 'manual']),
  requestedBy: Ref.nullable(),
  entriesChecked: Count,
  lastPosition: Count,
  ok: z.boolean(),
  breaks: Count,
  /** The first few positions that didn't match, and why. */
  firstBreaks: z.array(z.object({ position: Count, problem: z.string() })),
});
export type AuditChainCheck = z.infer<typeof AuditChainCheck>;

export const AuditChainStatus = z.object({
  lastCheck: AuditChainCheck.nullable(),
  /** A check an Admin asked for that hasn't run yet. */
  pending: z.object({ requestedAt: DateTime, requestedBy: Ref.nullable() }).nullable(),
  entriesNow: Count,
  /** Entries written since the last check (they're checked by the next one). */
  entriesSinceCheck: Count,
});
export type AuditChainStatus = z.infer<typeof AuditChainStatus>;

/** A record's whole trail, newest first (a person's Record history, a list's History). */
export const AuditTrail = z.object({ items: z.array(AuditEntry) });
export type AuditTrail = z.infer<typeof AuditTrail>;

// ---------------------------------------------------------------------------------------------
// Preview as (TEST1) and Preview & testing (SYS8)
// ---------------------------------------------------------------------------------------------

/** Roles a person can preview on top of Employee, which everyone has. */
export const PreviewRole = z.enum([
  'manager',
  'sector_lead',
  'delegate',
  'executive_delegate',
  'executive',
  'stock_admin',
  'payroll',
  'finance',
  'system_admin',
  'support',
]);
export type PreviewRole = z.infer<typeof PreviewRole>;

/** The roles that work in one sector, so a preview of them can be scoped to it (APP-059). */
export const PREVIEW_SCOPED_ROLES = ['sector_lead', 'delegate'] as const satisfies PreviewRole[];

/** Start Preview as a role combination, or change the one being previewed. */
export const StartSamplePreview = z
  .object({
    roles: z
      .array(PreviewRole)
      .max(10)
      .refine((r) => new Set(r).size === r.length, 'Pick each role once.'),
    /**
     * "Sector Lead for Maritime Airborne": the sector a previewed Sector Lead or Delegate works
     * in. Optional; only with one of those roles. The server checks the sector exists.
     */
    sectorId: z.string().trim().min(1).max(100).optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.sectorId === undefined ||
      v.roles.some((r) => (PREVIEW_SCOPED_ROLES as readonly string[]).includes(r)),
    { message: 'A sector applies only to a Sector Lead or Delegate.', path: ['sectorId'] },
  );

/** APP-122 (dev and test only): act as this real person, with their live data and authority. */
export const StartPersonPreview = z.object({ personId: z.uuid() }).strict();

/** A role combination over sample data, or (dev and test only) a real person. */
export const StartPreview = z.union([StartSamplePreview, StartPersonPreview]);
export type StartPreview = z.infer<typeof StartPreview>;

/** A real person a tester can act as (APP-122), as the panel lists them. */
export const PreviewPerson = z.object({
  id: z.string(),
  name: z.string(),
  jobTitle: z.string().nullable(),
  /** Their sector: the one they lead or act in for a Sector Lead or Delegate, else their own. */
  sector: Ref.nullable(),
  /** Why they're listed, when the job title doesn't say: "For Graham Whitlock". */
  note: z.string().nullable(),
});
export type PreviewPerson = z.infer<typeof PreviewPerson>;

/** One role's real holders, the default first (APP-122 §6). Empty when nobody holds it. */
export const PreviewPick = z.object({ role: RoleKey, people: z.array(PreviewPerson) });
export type PreviewPick = z.infer<typeof PreviewPick>;

/**
 * APP-228: someone with a Spot decision waiting now, and how many of each kind. Counts only:
 * never which nominations, who is nominated, or for how much.
 */
export const PreviewSpotWaiting = PreviewPerson.extend({
  /** Nominees waiting on them as their current supervisor. */
  supervisorDecisions: z.number().int().nonnegative(),
  /** Spot funding requests waiting on the Executives, any one of whom decides each. */
  fundingRequests: z.number().int().nonnegative(),
}).strict();
export type PreviewSpotWaiting = z.infer<typeof PreviewSpotWaiting>;

/** `GET /api/v1/me/preview/people?q=`: anyone active, found by name (APP-122). */
export const PreviewPeopleQuery = z.object({ q: z.string().trim().min(2).max(100) }).strict();
export const PreviewPeople = z.object({ people: z.array(PreviewPerson) });
export type PreviewPeople = z.infer<typeof PreviewPeople>;

export const PreviewStatus = z.object({
  canPreview: z.boolean(),
  /**
   * APP-122: the real person this session acts as (dev and test only), or null. While it's set,
   * `preview` is null: nothing is sample data. Optional for older readers.
   */
  actingAs: z.object({ person: Ref, startedAt: DateTime }).nullable().optional(),
  /** The combination being previewed (Employee first), or null when not previewing. */
  preview: z
    .object({
      roles: z.array(RoleKey),
      /** The sector a previewed Sector Lead or Delegate works in, or null. */
      sector: Ref.nullable(),
      startedAt: DateTime,
    })
    .nullable(),
});
export type PreviewStatus = z.infer<typeof PreviewStatus>;

/** What the Preview as panel (TEST1) opens with: the status and the sectors to scope to. */
export const PreviewPanel = PreviewStatus.extend({
  /** Sectors with a budget pool, in their usual order: the "Sector Lead for …" choices. */
  sectors: z.array(Ref),
  /**
   * APP-122: the real people each role names, where this environment lets testers act as a real
   * person (dev and test only). Null in production, or for someone who may not preview.
   * APP-228: `spotWaiting`, the people with a Spot decision waiting now, by name; empty when none.
   */
  realPeople: z
    .object({ picks: z.array(PreviewPick), spotWaiting: z.array(PreviewSpotWaiting) })
    .nullable()
    .optional(),
});
export type PreviewPanel = z.infer<typeof PreviewPanel>;

/** SYS8: the master switch and named people come from the `preview.access` setting. */
export const AdminPreview = z.object({
  enabled: z.boolean(),
  lastChanged: LastChanged.nullable(),
  people: z.array(
    z.object({
      person: PersonRef.extend({ roles: z.array(z.string()) }),
      grantedBy: Ref.nullable(),
      grantedAt: DateTime.nullable(),
      endsOn: PlainDate.nullable(),
      /** The end date is within the next 14 days. */
      endingSoon: z.boolean(),
      /** Past the end date: the grant no longer works, but stays listed until removed. */
      ended: z.boolean(),
    }),
  ),
  sessions: z.array(
    z.object({
      sessionId: z.uuid(),
      person: Ref,
      roles: z.array(RoleKey),
      /** The sector a Sector Lead or Delegate preview was scoped to ("… (Maritime Airborne)"). */
      sector: Ref.nullable().optional(),
      /** APP-122: the real person the session acted as; `roles` is empty then. */
      actingAs: Ref.nullable().optional(),
      startedAt: DateTime,
      endedAt: DateTime.nullable(),
      minutes: Count,
      inProgress: z.boolean(),
      /** How it ended: Exit preview, access was turned off or expired, or the session ended. */
      ended: z.enum(['exit', 'access_ended', 'session_ended']).nullable(),
    }),
  ),
  /**
   * Which environment this is (SYS8 names it: "Dev · Azure Government"). Each environment keeps
   * its own Preview setting and list. Null where the server wasn't told (a test harness).
   */
  environment: z
    .object({
      appEnv: z.enum(['local', 'dev', 'test', 'prod']),
      cloud: z.enum(['AzureUSGovernment', 'AzureCloud']),
    })
    .nullable(),
  /**
   * This deployment has the setup sandbox (APP-096), so the page shows its panel. Elsewhere the
   * panel's route doesn't exist and the page never asks for it (QA-057).
   */
  setupSandbox: z.boolean(),
});
export type AdminPreview = z.infer<typeof AdminPreview>;

// ---------------------------------------------------------------------------------------------
// People (SYS6) and the person record (SYS3)
// ---------------------------------------------------------------------------------------------

/** Synced fields a System Admin may override (PRD §19). Work email and sign-in name never. */
export const OverridableField = z.enum([
  'firstName',
  'lastName',
  'displayName',
  'jobTitle',
  'office',
  'manager',
  'status',
]);
export type OverridableField = z.infer<typeof OverridableField>;

export const PersonStatusFilter = z.enum([
  'all',
  'active',
  'needs_setup',
  'left',
  'manual',
  'hidden',
]);
export type PersonStatusFilter = z.infer<typeof PersonStatusFilter>;

/** A MyRewards role, or `tester` for the people named in Preview & testing. */
export const PersonRoleFilter = z.enum([...RoleKey.options, 'tester']);

export const AdminPeopleQuery = z
  .object({
    /** Name, email or EE#. */
    q: z.string().trim().min(1).max(200).optional(),
    /** A sector ID, or `none` for people with no sector yet. */
    sector: z.string().trim().min(1).max(100).optional(),
    role: PersonRoleFilter.optional(),
    status: PersonStatusFilter.default('all'),
    ...pageFields,
  })
  .strict();
export type AdminPeopleQuery = z.infer<typeof AdminPeopleQuery>;

/** The same filters, for the whole list as CSV (SYS6 "Export CSV"). */
export const AdminPeopleExportQuery = AdminPeopleQuery.omit({ page: true, size: true });
export type AdminPeopleExportQuery = z.infer<typeof AdminPeopleExportQuery>;

/** One role tag on a person: scoped where the role is scoped. */
export const PersonRoleTag = z.object({
  role: PersonRoleFilter,
  sector: Ref.nullable(),
  /** Delegates: the Sector Lead they enter awards for. */
  forPerson: Ref.nullable(),
});
export type PersonRoleTag = z.infer<typeof PersonRoleTag>;

const CostType = z.enum(['OH', 'GA']);
const MissingField = z.enum(['sector', 'homeOrg', 'costType']);

export const AdminPersonRow = z.object({
  id: z.uuid(),
  /** `P-000318`: the MyRewards person ID people read; it never changes. */
  personNumber: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  eeNumber: z.string().nullable(),
  jobTitle: z.string().nullable(),
  department: z.string().nullable(),
  homeOrg: z.string().nullable(),
  manager: Ref.nullable(),
  sector: Ref.nullable(),
  costType: CostType.nullable(),
  status: z.enum(['active', 'left']),
  origin: z.enum(['synced', 'manual']),
  showInSearch: z.boolean(),
  /** Active, and sector, home org or OH/G&A still missing. */
  needsSetup: z.boolean(),
  missing: z.array(MissingField),
  roles: z.array(PersonRoleTag),
  overridden: z.array(OverridableField),
  lastSyncedAt: DateTime.nullable(),
  /** In an open unsure match or EE# conflict, or a manual record sharing a synced one's name. */
  likelyDuplicate: z.boolean(),
});
export type AdminPersonRow = z.infer<typeof AdminPersonRow>;

export const AdminPeoplePage = z.object({
  items: z.array(AdminPersonRow),
  page: PageInfo,
  /** Everyone under the search, sector and role filters, by status chip. */
  counts: z.object({
    all: Count,
    active: Count,
    needsSetup: Count,
    left: Count,
    manual: Count,
    hidden: Count,
  }),
  /** Everyone under every filter ("Showing 18 of 513"). */
  total: Count,
  /** Every sector, in their order: the Sector filter's choices. */
  sectors: z.array(Ref),
});
export type AdminPeoplePage = z.infer<typeof AdminPeoplePage>;

/** Every field on a person record (SYS3), with where it comes from and how it changes. */
export const PersonFieldKey = z.enum([
  'personNumber',
  'entraObjectId',
  'eeNumber',
  'firstName',
  'lastName',
  'displayName',
  'workEmail',
  'upn',
  'jobTitle',
  'department',
  'office',
  'manager',
  'sector',
  'homeOrg',
  'costType',
  'status',
  'showInSearch',
  'hireDate',
  'serviceDate',
  'lastSynced',
  'lastUpdated',
]);
export type PersonFieldKey = z.infer<typeof PersonFieldKey>;

export const AdminPersonField = z.object({
  key: PersonFieldKey,
  /** Text, a date, a boolean, or `{ id, name }` for the manager and sector. */
  value: z.unknown(),
  /** Where the value comes from: the app, Entra ID, a CSV import, or typed in (a manual record). */
  source: z.enum(['app', 'entra', 'csv', 'manual']),
  /**
   * How it changes: `never`, `override` (a System Admin overrides the synced value), `app`
   * (edited in MyRewards; the sync never writes it), `manual` (a manual record's own field), or
   * `sync` (written by the sync and fixed in Entra).
   */
  changes: z.enum(['never', 'override', 'app', 'manual', 'sync']),
  override: z
    .object({
      syncedValue: z.unknown(),
      reason: z.string(),
      setBy: Ref.nullable(),
      setAt: DateTime,
    })
    .nullable(),
  lastChanged: LastChanged.nullable(),
});
export type AdminPersonField = z.infer<typeof AdminPersonField>;

export const AdminPersonRole = PersonRoleTag.extend({
  /** Who granted it and when; null for derived roles (Manager) and the Tester list. */
  grantedBy: Ref.nullable(),
  grantedAt: DateTime.nullable(),
  /** Where it comes from: a list or grant kept in Roles & lists, a Sector Lead's delegation, the directory, or Preview & testing. */
  from: z.enum(['list', 'grant', 'delegation', 'directory', 'preview']),
});
export type AdminPersonRole = z.infer<typeof AdminPersonRole>;

export const AdminPerson = z.object({
  id: z.uuid(),
  personNumber: z.string(),
  name: z.string(),
  initials: z.string(),
  jobTitle: z.string().nullable(),
  sector: Ref.nullable(),
  origin: z.enum(['synced', 'manual']),
  status: z.enum(['active', 'left']),
  showInSearch: z.boolean(),
  needsSetup: z.boolean(),
  missing: z.array(MissingField),
  /** False when the directory has said they left, whatever an override shows (APP-040). */
  canSignIn: z.boolean(),
  entra: z
    .object({
      /** The object ID shortened (`a91f…3c07`): enough to recognise, not to copy around. */
      objectIdHint: z.string(),
      linkedAt: DateTime,
      linkedBy: z.string(),
      lastSyncAt: DateTime.nullable(),
    })
    .nullable(),
  /** A record a merge retired (APP-113 §7d): the one it was merged into. */
  mergedInto: Ref.nullable().default(null),
  fields: z.array(AdminPersonField),
  roles: z.array(AdminPersonRole),
  awards: z.object({
    /** Awards with a line for this person: still open, and all. */
    received: z.object({ open: Count, total: Count }),
    /** Awards this person entered (or that were entered for them as Sector Lead). */
    entered: z.object({ open: Count, total: Count }),
  }),
  canEdit: z.object({
    /** Override synced fields, hide the record, edit a manual record (System Admin). */
    record: z.boolean(),
    /** Sector, home org, OH/G&A and EE# (Stock Admin or System Admin). */
    appFields: z.boolean(),
    /** Roles and lists (System Admin). */
    roles: z.boolean(),
  }),
});
export type AdminPerson = z.infer<typeof AdminPerson>;

const ShortText = z.string().trim().min(1).max(200);

export const SetOverride = z.object({ value: z.unknown(), reason: Reason }).strict();
export type SetOverride = z.infer<typeof SetOverride>;

export const ClearOverride = z.object({ reason: Reason }).strict();
export type ClearOverride = z.infer<typeof ClearOverride>;

/** App-owned fields (PRD §19): what clears "needs setup". Send only what changes. */
export const SetAppFields = z
  .object({
    sectorId: z.string().trim().min(1).max(100).nullable().optional(),
    homeOrg: z.string().trim().min(1).max(40).nullable().optional(),
    costType: CostType.nullable().optional(),
    /** Kept here until ADP; unique when filled in. Text: leading zeros stay. */
    eeNumber: z.string().trim().min(1).max(40).nullable().optional(),
    reason: OptionalReason,
  })
  .strict()
  .refine(
    (v) =>
      v.sectorId !== undefined ||
      v.homeOrg !== undefined ||
      v.costType !== undefined ||
      v.eeNumber !== undefined,
    'Change at least one field.',
  );
export type SetAppFields = z.infer<typeof SetAppFields>;

const ManualFields = {
  firstName: ShortText,
  lastName: ShortText,
  displayName: ShortText.optional(),
  workEmail: z.email().max(200).nullable().optional(),
  /**
   * Their Entra sign-in (UPN), so the sync or an Admin can connect the record to Entra (APP-113
   * §4b). Never used to sign in: sign-in matches the Entra object ID only.
   */
  signInName: z.string().trim().toLowerCase().pipe(z.email().max(256)).nullable().optional(),
  jobTitle: ShortText.nullable().optional(),
  department: ShortText.nullable().optional(),
  office: ShortText.nullable().optional(),
  managerId: z.uuid().nullable().optional(),
  status: z.enum(['active', 'left']).optional(),
  hireDate: PlainDate.nullable().optional(),
  serviceDate: PlainDate.nullable().optional(),
};

/** Someone who isn't in Entra ID, such as a former employee (PRD §19 "Manual records"). */
export const CreateManualPerson = z
  .object({
    ...ManualFields,
    eeNumber: z.string().trim().min(1).max(40).nullable().optional(),
    sectorId: z.string().trim().min(1).max(100).nullable().optional(),
    homeOrg: z.string().trim().min(1).max(40).nullable().optional(),
    costType: CostType.nullable().optional(),
    reason: OptionalReason,
  })
  .strict();
export type CreateManualPerson = z.infer<typeof CreateManualPerson>;

/** A manual record's own fields; the sync never touches them. Send only what changes. */
export const UpdateManualPerson = z
  .object({
    firstName: ShortText.optional(),
    lastName: ShortText.optional(),
    displayName: ShortText.optional(),
    workEmail: ManualFields.workEmail,
    signInName: ManualFields.signInName,
    jobTitle: ManualFields.jobTitle,
    department: ManualFields.department,
    office: ManualFields.office,
    managerId: ManualFields.managerId,
    status: ManualFields.status,
    hireDate: ManualFields.hireDate,
    serviceDate: ManualFields.serviceDate,
    reason: OptionalReason,
  })
  .strict();
export type UpdateManualPerson = z.infer<typeof UpdateManualPerson>;

/** "Show in search" off hides someone from search and pickers; they stay in history and reports. */
export const SetVisibility = z.object({ showInSearch: z.boolean(), reason: Reason }).strict();
export type SetVisibility = z.infer<typeof SetVisibility>;

/** "What can this person do" (SYS2): roles, groups, lists, delegations and settings areas. */
export const SettingsAreaKey = z.enum([
  'budgets',
  'values',
  'charge_codes',
  'sector_setup',
  'requesters',
  'elections_letters',
  'directory_roles',
  'system',
]);
export type SettingsAreaKey = z.infer<typeof SettingsAreaKey>;

const AreaAccess = z.object({
  access: z.enum(['none', 'view', 'edit']),
  /** Edits only some of the area (a Stock Admin edits FMV, not the split). */
  partial: z.boolean(),
  /** The settings in the area this access covers, by registry key. */
  editable: z.array(z.string()),
});

export const PersonAccess = z.object({
  person: PersonRef,
  roles: z.array(RoleAssignment),
  groups: z.array(
    z.enum([
      'executives',
      'sector_leads',
      'stock_admin_team',
      'payroll_team',
      'finance',
      'system_admins',
      'executive_delegates',
      'testers',
    ]),
  ),
  lists: z.object({ executive: z.boolean(), sectorLeadOf: z.array(Ref) }),
  /** APP-274: the Executives this person enters awards for, and on what terms. */
  executiveDelegations: z.array(
    z.object({
      forPerson: Ref,
      awardTypes: z.array(z.enum(['EDB', 'SDB', 'PAA', 'TAA'])),
      needsApproval: z.boolean(),
    }),
  ),
  delegations: z.array(
    z.object({
      sector: Ref,
      forPerson: Ref,
      awardTypes: z.array(z.enum(['SDB', 'PAA', 'TAA'])),
      needsApproval: z.boolean(),
    }),
  ),
  tester: z.object({ allowed: z.boolean(), endsOn: PlainDate.nullable() }),
  /**
   * What they can start on Recognize (APP-106, APP-111), as the create rule works it out from their
   * roles, delegations, home sector and each award type's Recognize audience: the same list
   * Recognize gives them. A Sector bonus, PAA or TAA names its sectors; an Executive award and a
   * Spot nomination have none. `named`: a System Admin named them in that type's audience.
   */
  canStart: z.array(
    z.object({ type: OperationalAwardTypeCode, sectors: z.array(Ref), named: z.boolean() }),
  ),
  /** The Level 1 items they see. */
  nav: z.array(LevelOne),
  settingsAreas: z.array(
    AreaAccess.extend({
      area: SettingsAreaKey,
      /** The same area, one role at a time ("As Stock Admin", "With System Admin"). */
      byRole: z.array(AreaAccess.extend({ role: RoleKey })),
    }),
  ),
  auditLog: z.object({
    scope: z.enum(['all', 'sector', 'financial', 'payroll', 'none']),
    /** Several apply when roles blend (a Sector Lead who is also in Finance). */
    scopes: z.array(z.enum(['all', 'sector', 'financial', 'payroll'])),
    sectors: z.array(Ref),
  }),
});
export type PersonAccess = z.infer<typeof PersonAccess>;

// ---------------------------------------------------------------------------------------------
// Roles & lists (SYS2)
// ---------------------------------------------------------------------------------------------

export const GrantableRole = z.enum([
  'stock_admin',
  'payroll',
  'finance',
  'system_admin',
  // Q27: approving PAAs and TAAs is separately grantable to a non-Executive.
  'paa_taa_approver',
  'spot_reviewer',
  'support',
]);
export type GrantableRole = z.infer<typeof GrantableRole>;

export const ListMember = z.object({
  person: PersonRef.extend({ active: z.boolean() }),
  addedAt: DateTime,
  addedBy: Ref.nullable(),
  reason: z.string().nullable(),
});
export type ListMember = z.infer<typeof ListMember>;

export const AdminRoles = z.object({
  spotCommitteeConfigured: z.boolean().optional(),
  executives: z.object({ members: z.array(ListMember), lastChanged: LastChanged.nullable() }),
  sectorLeads: z.array(
    z.object({
      sector: Ref,
      lead: ListMember.nullable(),
      delegates: z.array(Ref),
      lastChanged: LastChanged.nullable(),
    }),
  ),
  grants: z.array(
    z.object({
      role: GrantableRole,
      members: z.array(ListMember),
      lastChanged: LastChanged.nullable(),
    }),
  ),
  /** The Tester row: people named in Preview & testing (managed there). */
  testers: z.object({
    enabled: z.boolean(),
    people: z.array(Ref),
    lastChanged: LastChanged.nullable(),
  }),
  /**
   * APP-148: what Stock Admins did for others in the last 30 days, counted as decisions and
   * changes (one per thing done for someone), never as audit entries: done for each Executive and
   * Sector Lead, and by each Stock Admin, by person ID. Someone with none isn't listed. Optional:
   * older servers omit it.
   */
  actedFor30: z
    .object({
      /**
       * The first day counted (the organization day 29 days back): worked out once, here, so the
       * counts' Audit log links open from exactly this day (R105).
       */
      from: PlainDate,
      doneFor: z.record(z.string(), Count),
      doneBy: z.record(z.string(), Count),
    })
    .optional(),
});
export type AdminRoles = z.infer<typeof AdminRoles>;

export const ChangeList = ReasonOnly;
export type ChangeList = z.infer<typeof ChangeList>;

export const SetSectorLead = z.object({ personId: z.uuid(), reason: OptionalReason }).strict();
export type SetSectorLead = z.infer<typeof SetSectorLead>;

export const RoleHistoryQuery = z
  .object({
    list: z.enum(['executives', 'requesters', 'sector_lead', 'grant']),
    /** The sector for `sector_lead`; the role for `grant`. */
    key: z.string().trim().min(1).max(100).optional(),
  })
  .strict();
export type RoleHistoryQuery = z.infer<typeof RoleHistoryQuery>;

// ---------------------------------------------------------------------------------------------
// Directory & Entra sync (SYS3)
// ---------------------------------------------------------------------------------------------

export const SyncRun = z.object({
  id: z.uuid(),
  trigger: z.enum(['schedule', 'manual', 'seed']),
  /** `cancelled`: the sync was switched off before this run changed anything (not an error). */
  status: z.enum(['queued', 'running', 'succeeded', 'failed', 'cancelled']),
  requestedBy: Ref.nullable(),
  startedAt: DateTime,
  finishedAt: DateTime.nullable(),
  counts: z.object({
    checked: Count,
    created: Count,
    updated: Count,
    deactivated: Count,
    skipped: Count,
    issues: Count,
  }),
  /** A plain description of what went wrong, or why it was cancelled; never a stack trace. */
  error: z.string().nullable(),
});
export type SyncRun = z.infer<typeof SyncRun>;

export const SyncRunsQuery = z
  .object({
    ...pageFields,
  })
  .strict();
export type SyncRunsQuery = z.infer<typeof SyncRunsQuery>;

export const SyncRunsPage = z.object({
  items: z.array(SyncRun),
  page: PageInfo,
});
export type SyncRunsPage = z.infer<typeof SyncRunsPage>;

export const SyncIssueKind = z.enum([
  'unsure_match',
  'ee_conflict',
  'left_scope',
  'deactivation_guard',
  /** An Entra sign-in with no record yet, waiting for an Admin to connect it (APP-113 §5). */
  'unknown_sign_in',
]);

export const SyncIssue = z.object({
  id: z.uuid(),
  kind: SyncIssueKind,
  status: z.enum(['open', 'resolved', 'dismissed']),
  /** The MyRewards record it's about (the candidate, for an unsure match). */
  person: Ref.nullable(),
  /** The Entra record's name as the directory gives it. */
  directoryName: z.string().nullable(),
  objectIdHint: z.string().nullable(),
  /** Unsure match: the matching rule's field; whether the surnames agree. */
  matchedBy: z.enum(['ee_number', 'upn', 'work_email', 'name']).nullable(),
  surnameMatches: z.boolean().nullable(),
  /** Unsure match: why the sync asked instead of connecting (APP-113 §6, §11b). */
  why: z
    .enum([
      'rule_asks',
      'last_name_differs',
      'several',
      'claimed',
      'sign_in_name_taken',
      'ee_number_taken',
      'left_record',
      'guest',
    ])
    .nullable(),
  /** A sign-in waiting for an Admin (APP-113 §5): what the token said, and why it waits. */
  signIn: z
    .object({
      signInName: z.string().nullable(),
      email: z.string().nullable(),
      attempts: Count,
      firstSeenAt: DateTime,
      lastSeenAt: DateTime,
      waitingBecause: z.enum(['guest', 'off', 'sync_decides', 'lookalike']),
    })
    .nullable(),
  /**
   * Records a waiting sign-in looks like: the suggested connections, with what they share and
   * whether the record is marked as left, so an Admin never connects a namesake by accident.
   */
  lookalikes: z.array(
    Ref.extend({
      matchedOn: z.array(z.enum(['sign_in_name', 'work_email', 'name'])),
      left: z.boolean(),
    }),
  ),
  /** Left the sync: still in Entra, left out by this filter, in words (APP-113 §12g). */
  filteredBy: z.string().nullable().default(null),
  /** EE# conflict: the number and who already holds it. */
  eeNumber: z.string().nullable(),
  heldBy: Ref.nullable(),
  /** Deactivation guard: how many the run would have deactivated, and the limit. */
  wouldDeactivate: Count.nullable(),
  limit: Count.nullable(),
  /** The run that raised it; null for a sign-in waiting for an Admin. */
  runId: z.uuid().nullable(),
  raisedAt: DateTime,
  resolvedBy: Ref.nullable(),
  resolvedAt: DateTime.nullable(),
  reason: z.string().nullable(),
});
export type SyncIssue = z.infer<typeof SyncIssue>;

export const SyncRunDetail = SyncRun.extend({
  /** Every change the run wrote (its audit batch), up to 1,000. */
  changes: z.array(AuditEntry),
  changesTruncated: z.boolean(),
  issues: z.array(SyncIssue),
});
export type SyncRunDetail = z.infer<typeof SyncRunDetail>;

export const SyncStatus = z.object({
  /** A directory is connected in this environment, so Run now can work. */
  available: z.boolean(),
  /** The `directory.sync` setting: on or off, and when it runs. */
  enabled: z.boolean(),
  schedule: z.string(),
  employeesOnly: z.boolean(),
  lastRun: SyncRun.nullable(),
  lastSuccess: SyncRun.nullable(),
  /** A run waiting or under way. */
  current: SyncRun.nullable(),
  openIssues: Count,
  needsSetup: Count,
  /**
   * "Who the sync brings in" has been saved (APP-131 §2). Until it is, a run brings in nobody:
   * the sync reads the whole tenant, so someone chooses first.
   */
  filtersChosen: z.boolean(),
  /** The filter groups' names as Entra had them at the newest read, by object ID (APP-131 §4). */
  groupNames: z.record(z.string(), z.string()),
});
export type SyncStatus = z.infer<typeof SyncStatus>;

/** Resolve an issue (for an unsure match: link the two records) or dismiss it, with a reason. */
export const ResolveSyncIssue = z
  .object({
    outcome: z.enum(['resolved', 'dismissed']),
    reason: Reason,
    /** A waiting sign-in (APP-113 §5) resolved by connecting it to this record… */
    connectTo: z.uuid().optional(),
    /** …or by adding them as a new person. */
    addPerson: z.literal(true).optional(),
  })
  .strict()
  .refine((r) => r.connectTo === undefined || r.addPerson === undefined, {
    path: ['addPerson'],
  });
export type ResolveSyncIssue = z.infer<typeof ResolveSyncIssue>;

/**
 * Connecting one record to Entra (APP-113 §4c, §7): the accounts the sync is holding for it, the
 * sign-ins waiting that look like it, the records that may be the same person (and what stops a
 * merge either way), and whether an object ID can be typed.
 */
export const ConnectOptions = z.object({
  person: z.object({
    id: z.uuid(),
    name: z.string(),
    origin: z.enum(['synced', 'manual']),
    /** The first block of the connected account's object ID, or null when not connected. */
    objectIdHint: z.string().nullable(),
    mergedInto: Ref.nullable(),
    /** Marked as left: connecting or keeping it doesn't let anyone sign in until it's active. */
    left: z.boolean(),
  }),
  held: z.array(
    z.object({
      issueId: z.uuid(),
      directoryName: z.string().nullable(),
      objectIdHint: z.string().nullable(),
      matchedBy: z.enum(['ee_number', 'upn', 'work_email', 'name']).nullable(),
    }),
  ),
  signIns: z.array(
    z.object({
      issueId: z.uuid(),
      name: z.string().nullable(),
      signInName: z.string().nullable(),
      lastSeenAt: DateTime,
      matchedOn: z.array(z.enum(['sign_in_name', 'work_email', 'name'])),
    }),
  ),
  duplicates: z.array(
    z.object({
      person: Ref.extend({
        origin: z.enum(['synced', 'manual']),
        connected: z.boolean(),
        left: z.boolean(),
      }),
      why: z.enum(['same_name', 'same_email']),
      /** Why that record can't be merged into this one, in a sentence; null when it can. */
      keepThis: z.string().nullable(),
      /** Why this record can't be merged into that one. */
      keepThat: z.string().nullable(),
    }),
  ),
  canTypeObjectId: z.boolean(),
});
export type ConnectOptions = z.infer<typeof ConnectOptions>;

export const ConnectEntra = z
  .object({
    source: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('issue'), issueId: z.uuid() }).strict(),
      z
        .object({ kind: z.literal('objectId'), objectId: z.string().trim().min(1).max(64) })
        .strict(),
    ]),
    reason: Reason,
  })
  .strict();
export type ConnectEntra = z.infer<typeof ConnectEntra>;

export const DisconnectEntra = z.object({ reason: Reason }).strict();
export type DisconnectEntra = z.infer<typeof DisconnectEntra>;

/** Merge `otherId` into the record in the path, which is kept (APP-113 §7d). */
export const MergePeople = z.object({ otherId: z.uuid(), reason: Reason }).strict();
export type MergePeople = z.infer<typeof MergePeople>;

/** Two records that look alike are different people: never suggested again. */
export const NotSamePerson = z
  .object({ personIds: z.tuple([z.uuid(), z.uuid()]), reason: Reason })
  .strict();
export type NotSamePerson = z.infer<typeof NotSamePerson>;

/** Connect all suggested (APP-113 §11d): the waiting sign-ins the Admin saw, with one reason. */
export const ConnectSuggested = z
  // The sign-ins the Admin saw: one page of the report holds at most 200.
  .object({ issueIds: z.array(z.uuid()).min(1).max(1000), reason: Reason })
  .strict();
export type ConnectSuggested = z.infer<typeof ConnectSuggested>;

export const ConnectSuggestedResult = z.object({
  connected: z.array(z.object({ issueId: z.uuid(), person: Ref })),
  skipped: z.array(z.object({ issueId: z.uuid(), why: z.string() })),
});
export type ConnectSuggestedResult = z.infer<typeof ConnectSuggestedResult>;

/** What a sync run would do to one account, in a preview (APP-113 §2). */
export const PreviewOutcome = z.enum([
  'add',
  'connect',
  'update',
  'unchanged',
  'ask',
  'left_out',
  'mark_left',
  'connected_outside',
]);
export type PreviewOutcome = z.infer<typeof PreviewOutcome>;

export const PreviewAccount = z.object({
  oid: z.string(),
  name: z.string().nullable(),
  signInName: z.string().nullable(),
  employeeId: z.string().nullable(),
  outcome: PreviewOutcome,
  /**
   * Why, as a code: the rule that matched (`eeNumber`, `signInName`, `workEmail`, `name`), why it
   * asks (`last_name_differs`…, `guest`), why it's left out (`guest`, `contractor`,
   * `service_or_resource`, `not_a_person`, or a filter rule already in words), why it's marked as
   * left (`left_scope`, `account_disabled`, or the filter in words), or `ee_conflict`.
   * Accounts left unchanged are only counted; each outcome lists at most 500, by name.
   */
  reason: z.string().nullable(),
  person: Ref.nullable(),
  fields: z.array(z.string()),
});
export type PreviewAccount = z.infer<typeof PreviewAccount>;

/** A preview: the run the sync would make with these settings, without changing anyone. */
export const SyncPreview = z.object({
  id: z.uuid(),
  status: z.enum(['queued', 'running', 'succeeded', 'failed', 'cancelled']),
  requestedAt: DateTime,
  finishedAt: DateTime.nullable(),
  requestedBy: Ref.nullable(),
  /** Asked for with settings that weren't saved yet. */
  draft: z.boolean(),
  counts: z.record(PreviewOutcome, Count),
  accounts: z.array(PreviewAccount),
  /** The run would stop at the mass-change guard instead of marking this many as left. */
  guard: z.object({ wouldDeactivate: Count, limit: Count }).nullable(),
  error: z.string().nullable(),
});
export type SyncPreview = z.infer<typeof SyncPreview>;

/** Preview with these settings; whatever isn't given is the saved setting. */
export const RequestSyncPreview = z
  .object({
    filters: z.unknown().optional(),
    matching: z.unknown().optional(),
    employeesOnly: z.boolean().optional(),
  })
  .strict();
export type RequestSyncPreview = z.infer<typeof RequestSyncPreview>;

export const LatestSyncPreview = z.object({ preview: SyncPreview.nullable() });
export type LatestSyncPreview = z.infer<typeof LatestSyncPreview>;

/** The discrepancy report's open issues are paged; the rest of the report is whole (APP-135). */
export const DiscrepanciesQuery = z.object({ ...pageFields }).strict();
export type DiscrepanciesQuery = z.infer<typeof DiscrepanciesQuery>;

/** The discrepancy report (PRD §19): what to fix in Entra or set up here. */
export const Discrepancies = z.object({
  /**
   * One page of the open issues the report lists as rows: sign-ins waiting and the sync's own.
   * (The unsure matches and EE# conflicts are in `likelyDuplicates`.)
   */
  issues: z.array(SyncIssue),
  page: PageInfo,
  /** Every open unsure match and EE# conflict, so each likely duplicate can be reviewed. */
  duplicateIssues: z.array(SyncIssue),
  /** Overrides whose synced value differs, field by field: fix these in Entra, then clear them. */
  overrides: z.array(
    z.object({
      person: Ref,
      field: OverridableField,
      syncedValue: z.unknown(),
      shownValue: z.unknown(),
      reason: z.string(),
      setBy: Ref.nullable(),
      setAt: DateTime,
    }),
  ),
  /**
   * Who still needs a sector, home org or OH/G&A, newest first: on the screen the first few (the
   * rest page on People › Needs setup), in the export every one.
   */
  needsSetup: z.array(
    z.object({
      person: PersonRef,
      arrivedAt: DateTime,
      missing: z.array(MissingField),
    }),
  ),
  /** How many need setup in all (APP-135): the list above may be the first few. */
  needsSetupTotal: Count,
  likelyDuplicates: z.array(
    z.object({
      people: z.array(Ref),
      why: z.enum(['same_name', 'same_email', 'unsure_match', 'ee_conflict']),
      issueId: z.uuid().nullable(),
    }),
  ),
});
export type Discrepancies = z.infer<typeof Discrepancies>;

// ---------------------------------------------------------------------------------------------
// Console (SYS1) and the go-live checklist (FR5)
// ---------------------------------------------------------------------------------------------

export const GoLiveStepId = z.enum([
  'directory_sync',
  'app_fields',
  'executives',
  'sector_leads',
  'roles',
  'budgets',
  'values',
  'charge_codes',
  'letter_templates',
  'notifications',
  'sign_in',
  'history_import',
]);
export type GoLiveStepId = z.infer<typeof GoLiveStepId>;

export const GoLiveStep = z.object({
  id: GoLiveStepId,
  /** Worked out from what's in MyRewards now, never a stored tick. */
  status: z.enum(['done', 'in_progress', 'to_do', 'handed_off', 'optional']),
  required: z.boolean(),
  /** Counts and names behind the status, for the step's detail panel. */
  facts: z.array(
    z.object({ key: z.string(), value: z.unknown(), missing: z.boolean().default(false) }),
  ),
  lastChanged: LastChanged.nullable(),
});
export type GoLiveStep = z.infer<typeof GoLiveStep>;

export const GoLiveChecklist = z.object({
  steps: z.array(GoLiveStep),
  done: Count,
  total: Count,
  requiredLeft: Count,
  /** Every required step is done. */
  ready: z.boolean(),
  /** When MyRewards went live (`org.launchDate`), or null before go-live. */
  launch: z.object({ on: PlainDate, by: Ref.nullable(), at: DateTime }).nullable(),
  /** The welcome Go live sends: email only when its notification switch is on (off by default). */
  welcome: z.object({ email: z.boolean(), employees: Count }),
});
export type GoLiveChecklist = z.infer<typeof GoLiveChecklist>;

/** Go live (FR5): every required step done, with a reason. */
export const GoLive = z.object({ reason: Reason }).strict();
export type GoLive = z.infer<typeof GoLive>;

export const AdminConsole = z.object({
  people: z.object({
    total: Count,
    synced: Count,
    manual: Count,
    active: Count,
    left: Count,
    needsSetup: Count,
    hidden: Count,
  }),
  sync: SyncStatus,
  discrepancies: z.object({
    openIssues: Count,
    overridesDiffering: Count,
    likelyDuplicates: Count,
    /**
     * The Discrepancy report's rows, each once: overrides that differ, likely duplicates (the
     * sync's unsure matches and EE# conflicts among them) and the other open issues (QA-040).
     */
    total: Count,
  }),
  chain: AuditChainStatus,
  roles: z.object({
    systemAdmins: Count,
    executives: Count,
    sectorsWithoutLead: z.array(Ref),
  }),
  /** Award claims idle past the claim timeout: release them with a reason (support tools). */
  stuckClaims: z.array(
    z.object({
      award: z.object({ id: z.uuid(), displayId: z.string().nullable() }),
      claimedBy: Ref,
      lastActivityAt: DateTime,
      idleMinutes: Count,
    }),
  ),
  /** Awards with someone closed on them, for "Reopen a closed award" (none yet: a note instead). */
  closedAwards: Count,
  preview: z.object({ enabled: z.boolean(), people: Count }),
  ai: z.object({
    enabled: z.boolean(),
    provider: z.string(),
    model: z.string().nullable(),
    features: z.record(z.string(), z.boolean()),
  }),
  /** The ten areas' "Last changed by … on …" (the Audit log's is its latest entry). */
  settingsAreas: z.array(
    z.object({
      area: z.enum([...SettingsAreaKey.options, 'audit_log']),
      lastChanged: LastChanged.nullable(),
    }),
  ),
  goLive: z.object({ done: Count, total: Count, requiredLeft: Count, ready: z.boolean() }),
});
export type AdminConsole = z.infer<typeof AdminConsole>;
